<script setup>
import { computed, onMounted, ref } from 'vue';

const props = defineProps({
    registeredDomainId: { type: Number, required: true },
    domain: { type: String, required: true },
});

const detail = ref(null);
const presets = ref([]);
const records = ref([]);
const showAdvanced = ref(false);
const loadingRecords = ref(false);
const error = ref(null);

// preset confirm-modal state
const planning = ref(null); // { preset, changes, options }
const applying = ref(false);
const redirectForm = ref(null); // { preset, targetUrl }
const redirectFormError = ref(null);
const removingPresetKey = ref(null);
const switchingSiteDomain = ref(false);
const switchingEmailDomain = ref(false);
const retryingSiteActivation = ref(false);
const retryingEmailActivation = ref(false);

// advanced editor state
const editing = ref(null); // null | { mode: 'new' | 'edit', record }

const recordTypeOptions = [
    'A',
    'AAAA',
    'CNAME',
    'MX',
    'TXT',
    'SRV',
    'NS',
    'ANAME',
].map((t) => ({ value: t, label: t }));

const fetchAll = async () => {
    error.value = null;
    try {
        const [d, p, r] = await Promise.all([
            axios.get(
                route(
                    'settings.domains.manage.detail',
                    props.registeredDomainId
                )
            ),
            axios.get(
                route(
                    'settings.domains.manage.presets.index',
                    props.registeredDomainId
                )
            ),
            axios.get(
                route(
                    'settings.domains.manage.records.index',
                    props.registeredDomainId
                )
            ),
        ]);
        detail.value = d.data;
        presets.value = p.data.presets || [];
        records.value = r.data.records || [];
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.loadSettings');
    }
};

const fetchRecords = async () => {
    loadingRecords.value = true;
    try {
        const { data } = await axios.get(
            route(
                'settings.domains.manage.records.index',
                props.registeredDomainId
            )
        );
        records.value = data.records || [];
    } finally {
        loadingRecords.value = false;
    }
};

const fetchDetail = async () => {
    const { data } = await axios.get(
        route('settings.domains.manage.detail', props.registeredDomainId)
    );
    detail.value = data;

    return detail.value;
};

const fetchPresets = async () => {
    const { data } = await axios.get(
        route('settings.domains.manage.presets.index', props.registeredDomainId)
    );
    presets.value = data.presets || [];

    return presets.value;
};

const activeKeys = computed(() =>
    presets.value.filter((p) => p.is_active).map((p) => p.key)
);

const activePresetsWithSteps = computed(() =>
    presets.value.filter(
        (p) =>
            p.is_active &&
            Array.isArray(p.next_steps) &&
            p.next_steps.length > 0
    )
);

const isPresetBlocked = (preset) => {
    return preset.conflicts_with.some((k) => activeKeys.value.includes(k));
};

const shouldShowActive = (preset) => {
    if (preset.key === 'myflow_site') {
        return preset.is_active && detail.value?.is_in_use_for_site;
    }

    if (preset.key === 'myflow_email') {
        return preset.is_active && detail.value?.is_in_use_for_email;
    }

    return preset.is_active;
};

const presetTranslations = {
    myflow_site: {
        label: 'setting:domains.presets.myflowSite.label',
        description: 'setting:domains.presets.myflowSite.description',
    },
    myflow_email: {
        label: 'setting:domains.presets.myflowEmail.label',
        description: 'setting:domains.presets.myflowEmail.description',
    },
    google_workspace_email: {
        label: 'setting:domains.presets.googleWorkspaceEmail.label',
        description: 'setting:domains.presets.googleWorkspaceEmail.description',
    },
    microsoft_365_email: {
        label: 'setting:domains.presets.microsoft365Email.label',
        description: 'setting:domains.presets.microsoft365Email.description',
    },
    redirect: {
        label: 'setting:domains.presets.redirect.label',
        description: 'setting:domains.presets.redirect.description',
    },
    park: {
        label: 'setting:domains.presets.park.label',
        description: 'setting:domains.presets.park.description',
    },
};

const presetLabel = (preset) => {
    const key = presetTranslations[preset.key]?.label;

    if (key) return _mft(key);

    return typeof preset.label === 'string' && preset.label.includes(':')
        ? _mft(preset.label)
        : preset.label;
};

const presetDescription = (preset) => {
    const key = presetTranslations[preset.key]?.description;

    if (key) return _mft(key);

    return typeof preset.description === 'string' &&
        preset.description.includes(':')
        ? _mft(preset.description)
        : preset.description;
};

const presetNextStepTranslations = {
    google_workspace_email: {
        startTrial: {
            title: 'setting:domains.presets.googleWorkspaceEmail.nextSteps.startTrial.title',
            description:
                'setting:domains.presets.googleWorkspaceEmail.nextSteps.startTrial.description',
            cta: 'setting:domains.presets.googleWorkspaceEmail.nextSteps.startTrial.cta',
        },
        verifyDomain: {
            title: 'setting:domains.presets.googleWorkspaceEmail.nextSteps.verifyDomain.title',
            description:
                'setting:domains.presets.googleWorkspaceEmail.nextSteps.verifyDomain.description',
        },
        addDkim: {
            title: 'setting:domains.presets.googleWorkspaceEmail.nextSteps.addDkim.title',
            description:
                'setting:domains.presets.googleWorkspaceEmail.nextSteps.addDkim.description',
            cta: 'setting:domains.presets.googleWorkspaceEmail.nextSteps.addDkim.cta',
        },
        createUsers: {
            title: 'setting:domains.presets.googleWorkspaceEmail.nextSteps.createUsers.title',
            description:
                'setting:domains.presets.googleWorkspaceEmail.nextSteps.createUsers.description',
            cta: 'setting:domains.presets.googleWorkspaceEmail.nextSteps.createUsers.cta',
        },
    },
    microsoft_365_email: {
        signUp: {
            title: 'setting:domains.presets.microsoft365Email.nextSteps.signUp.title',
            description:
                'setting:domains.presets.microsoft365Email.nextSteps.signUp.description',
            cta: 'setting:domains.presets.microsoft365Email.nextSteps.signUp.cta',
        },
        verifyDomain: {
            title: 'setting:domains.presets.microsoft365Email.nextSteps.verifyDomain.title',
            description:
                'setting:domains.presets.microsoft365Email.nextSteps.verifyDomain.description',
        },
        addDkim: {
            title: 'setting:domains.presets.microsoft365Email.nextSteps.addDkim.title',
            description:
                'setting:domains.presets.microsoft365Email.nextSteps.addDkim.description',
            cta: 'setting:domains.presets.microsoft365Email.nextSteps.addDkim.cta',
        },
        createMailboxes: {
            title: 'setting:domains.presets.microsoft365Email.nextSteps.createMailboxes.title',
            description:
                'setting:domains.presets.microsoft365Email.nextSteps.createMailboxes.description',
            cta: 'setting:domains.presets.microsoft365Email.nextSteps.createMailboxes.cta',
        },
    },
};

const presetNextStepText = (preset, step, index, field) => {
    const key =
        presetNextStepTranslations[preset.key]?.[step.key]?.[field] ??
        Object.values(presetNextStepTranslations[preset.key] ?? {})[index]?.[
            field
        ];

    if (!key) {
        return field === 'cta' ? step.cta || step.url : step[field];
    }

    return _mft(key, { domain: props.domain });
};

const openPlan = async (preset, options = {}) => {
    try {
        const { data } = await axios.post(
            route('settings.domains.manage.presets.plan', [
                props.registeredDomainId,
                preset.key,
            ]),
            { options }
        );
        planning.value = {
            preset: data.preset,
            changes: data.changes,
            options,
        };
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.planPreset');
    }
};

const openRedirectForm = (preset) => {
    redirectFormError.value = null;
    redirectForm.value = {
        preset,
        targetUrl: preset.configuration?.target_url || '',
    };
};

const normalizedRedirectTarget = () => {
    let target = redirectForm.value?.targetUrl?.trim() || '';
    if (!target) {
        throw new Error(
            _mft('setting:domains.manage.redirect.validation.required')
        );
    }
    if (!/^https?:\/\//i.test(target)) {
        target = 'https://' + target;
    }

    let parsed;
    try {
        parsed = new URL(target);
    } catch {
        throw new Error(
            _mft('setting:domains.manage.redirect.validation.validUrl')
        );
    }

    if (!['http:', 'https:'].includes(parsed.protocol)) {
        throw new Error(
            _mft('setting:domains.manage.redirect.validation.validUrl')
        );
    }

    const source = props.domain.toLowerCase().replace(/\.$/, '');
    const targetHost = parsed.hostname.toLowerCase().replace(/\.$/, '');
    if ([source, `www.${source}`].includes(targetHost)) {
        throw new Error(
            _mft(
                'setting:domains.manage.redirect.validation.targetCannotBeDomain'
            )
        );
    }

    return target;
};

const submitRedirectForm = async () => {
    try {
        const targetUrl = normalizedRedirectTarget();
        const preset = redirectForm.value.preset;
        redirectForm.value = null;
        redirectFormError.value = null;
        await openPlan(preset, { target_url: targetUrl });
    } catch (e) {
        redirectFormError.value = e.message;
    }
};

const waitForPreset = async (predicate) => {
    for (let attempt = 0; attempt < 10; attempt += 1) {
        const current = await fetchPresets();
        if (predicate(current)) {
            return true;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    return false;
};

const applyPlan = async () => {
    if (
        !planning.value ||
        applying.value ||
        planning.value.changes.length === 0
    )
        return;

    applying.value = true;
    const presetKey = planning.value.preset.key;
    const targetUrl = planning.value.options.target_url;
    try {
        await axios.post(
            route('settings.domains.manage.presets.apply', [
                props.registeredDomainId,
                planning.value.preset.key,
            ]),
            { options: planning.value.options }
        );
        planning.value = null;
        const confirmed = await waitForPreset((current) => {
            const preset = current.find((item) => item.key === presetKey);
            if (!preset?.is_active) return false;

            return (
                presetKey !== 'redirect' ||
                preset.configuration?.target_url === targetUrl
            );
        });
        await Promise.all([fetchRecords(), fetchDetail()]);
        if (!confirmed) {
            error.value = _mft(
                'setting:domains.manage.error.changesQueuedNotConfirmed'
            );
        }
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.applyPreset');
    } finally {
        applying.value = false;
    }
};

const removePreset = async (preset) => {
    if (removingPresetKey.value) return;

    const message =
        preset.key === 'redirect'
            ? _mft('setting:domains.manage.confirm.turnOffRedirect')
            : preset.key === 'myflow_email'
            ? _mft('setting:domains.manage.confirm.turnOffMyflowEmail')
            : _mft('setting:domains.manage.confirm.turnOffMyflowSite');
    if (!confirm(message)) return;

    removingPresetKey.value = preset.key;
    error.value = null;
    try {
        await axios.delete(
            route('settings.domains.manage.presets.remove', [
                props.registeredDomainId,
                preset.key,
            ])
        );
        const confirmed = await waitForPreset((current) => {
            const refreshed = current.find((item) => item.key === preset.key);
            return refreshed && !refreshed.is_active;
        });
        await Promise.all([
            fetchRecords(),
            axios
                .get(
                    route(
                        'settings.domains.manage.detail',
                        props.registeredDomainId
                    )
                )
                .then(({ data }) => {
                    detail.value = data;
                }),
        ]);
        if (!confirmed) {
            error.value = _mft(
                'setting:domains.manage.error.removalQueuedNotConfirmed'
            );
        }
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.turnOffSetting');
    } finally {
        removingPresetKey.value = null;
    }
};

const startEditRecord = (record) => {
    editing.value = {
        mode: record ? 'edit' : 'new',
        record: record
            ? { ...record, host: record.host || '@' }
            : {
                  type: 'A',
                  host: '@',
                  answer: '',
                  ttl: 300,
                  priority: null,
              },
    };
};

const saveRecord = async () => {
    if (!editing.value) return;
    const r = editing.value.record;
    const payload = {
        type: r.type,
        host: r.host,
        answer: r.answer,
        ttl: Number(r.ttl) || 300,
        priority: ['MX', 'SRV'].includes(r.type)
            ? Number(r.priority) || 0
            : null,
    };

    try {
        if (editing.value.mode === 'new') {
            await axios.post(
                route(
                    'settings.domains.manage.records.create',
                    props.registeredDomainId
                ),
                payload
            );
        } else {
            await axios.put(
                route('settings.domains.manage.records.update', [
                    props.registeredDomainId,
                    r.id,
                ]),
                payload
            );
        }
        editing.value = null;
        await fetchRecords();
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.saveRecord');
    }
};

const deleteRecord = async (record) => {
    if (
        !confirm(
            _mft('setting:domains.manage.confirm.deleteRecord', {
                type: record.type,
                host: record.host || '@',
            })
        )
    )
        return;
    try {
        await axios.delete(
            route('settings.domains.manage.records.delete', [
                props.registeredDomainId,
                record.id,
            ])
        );
        await fetchRecords();
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.deleteRecord');
    }
};

const setAutoRenew = async (enabled) => {
    await axios.post(
        route('settings.domains.manage.auto-renew', props.registeredDomainId),
        { enabled }
    );
    detail.value.auto_renew = enabled;
};

const setWhoisPrivacy = async (enabled) => {
    await axios.post(
        route(
            'settings.domains.manage.whois-privacy',
            props.registeredDomainId
        ),
        { enabled }
    );
    detail.value.whois_privacy = enabled;
};

const useForSite = async () => {
    switchingSiteDomain.value = true;
    error.value = null;

    try {
        const { data } = await axios.post(
            route(
                'settings.domains.manage.use-for-site',
                props.registeredDomainId
            )
        );
        detail.value.is_in_use_for_site = data.is_in_use_for_site;
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.switchSiteDomain');
    } finally {
        switchingSiteDomain.value = false;
    }
};

const useForEmail = async () => {
    switchingEmailDomain.value = true;
    error.value = null;

    try {
        const { data } = await axios.post(
            route(
                'settings.domains.manage.use-for-email',
                props.registeredDomainId
            )
        );
        detail.value.is_in_use_for_email = data.is_in_use_for_email;
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.switchEmailDomain');
    } finally {
        switchingEmailDomain.value = false;
    }
};

const retryEmailActivation = async () => {
    retryingEmailActivation.value = true;
    error.value = null;

    try {
        const { data } = await axios.post(
            route(
                'settings.domains.manage.retry-email-activation',
                props.registeredDomainId
            )
        );
        detail.value.email_activation_pending = data.email_activation_pending;
        detail.value.email_activation_failed = data.email_activation_failed;
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.retryEmailVerification');
    } finally {
        retryingEmailActivation.value = false;
    }
};

const retrySiteActivation = async () => {
    retryingSiteActivation.value = true;
    error.value = null;

    try {
        const { data } = await axios.post(
            route(
                'settings.domains.manage.retry-site-activation',
                props.registeredDomainId
            )
        );
        detail.value.site_activation_pending = data.site_activation_pending;
        detail.value.site_activation_failed = data.site_activation_failed;
    } catch (e) {
        error.value =
            e?.response?.data?.message ||
            _mft('setting:domains.manage.error.retryWebsiteVerification');
    } finally {
        retryingSiteActivation.value = false;
    }
};

onMounted(fetchAll);
</script>

<template>
    <div class="mx-auto w-full max-w-4xl space-y-6 p-6">
        <frosted-bar
            :breadcrumbs="[
                {
                    url: route('settings.company.settings.domains'),
                    label: _mft('setting:domains.allDomains'),
                },
                { url: '', label: _mft('setting:domains.manage.title') },
            ]"
        >
            <div class="flex items-baseline justify-between">
                <h1>{{ domain }}</h1>
                <p v-if="detail" class="text-xs text-gray-500 mb-0">
                    {{ _mft('setting:domains.manage.status') }}:
                    <strong>{{ detail.status }}</strong> ·
                    {{ _mft('setting:domains.manage.renews') }}:
                    {{
                        detail.expires_at
                            ? new Date(detail.expires_at).toLocaleDateString()
                            : '—'
                    }}
                </p>
            </div>
        </frosted-bar>

        <div
            v-if="error"
            class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
            {{ error }}
        </div>

        <card-basic v-if="detail">
            <h2 class="text-base font-semibold">
                {{ _mft('setting:domains.manage.settings') }}
            </h2>
            <div class="mt-3 space-y-2 text-sm">
                <input-checkbox
                    :label="_mft('setting:domains.manage.autoRenew')"
                    :content="detail.auto_renew"
                    @update:content="setAutoRenew($event)"
                />
                <input-checkbox
                    :label="_mft('setting:domains.manage.whoisPrivacy')"
                    :content="detail.whois_privacy"
                    @update:content="setWhoisPrivacy($event)"
                />
            </div>
        </card-basic>

        <div>
            <h2 class="mb-3 text-base font-semibold">
                {{ _mft('setting:domains.buy.review.domainPurpose') }}
            </h2>
            <div class="grid gap-3 md:grid-cols-2">
                <card-basic
                    v-for="p in presets"
                    :key="p.key"
                    class="transition"
                    :class="{
                        '': p.is_active,
                        'opacity-50 cursor-not-allowed':
                            !p.is_active && isPresetBlocked(p),
                    }"
                >
                    <div class="flex items-start justify-between gap-3">
                        <div>
                            <div class="font-medium">{{ presetLabel(p) }}</div>
                            <p class="mt-1 text-xs text-gray-500">
                                {{ presetDescription(p) }}
                            </p>
                            <p
                                v-if="
                                    p.key === 'redirect' &&
                                    p.configuration?.target_url
                                "
                                class="mt-2 break-all text-xs text-gray-700"
                            >
                                {{ _mft('setting:domains.manage.redirectsTo') }}
                                <strong>{{
                                    p.configuration.target_url
                                }}</strong>
                            </p>
                            <p
                                v-if="!p.is_active && isPresetBlocked(p)"
                                class="mt-2 text-xs text-amber-700"
                            >
                                {{
                                    _mft(
                                        'setting:domains.manage.conflictingSetting'
                                    )
                                }}
                            </p>
                            <p
                                v-if="
                                    p.key === 'myflow_site' &&
                                    detail?.site_activation_failed
                                "
                                class="mt-2 text-xs text-red-700"
                            >
                                {{
                                    _mft(
                                        'setting:domains.manage.websiteVerificationTimedOut'
                                    )
                                }}
                            </p>
                            <p
                                v-if="
                                    p.key === 'myflow_email' &&
                                    detail?.email_activation_failed
                                "
                                class="mt-2 text-xs text-red-700"
                            >
                                {{
                                    _mft(
                                        'setting:domains.manage.emailVerificationTimedOut'
                                    )
                                }}
                            </p>
                        </div>
                        <span
                            v-if="shouldShowActive(p)"
                            class="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
                            >{{ _mft('setting:domains.manage.active') }}</span
                        >
                    </div>
                    <div class="mt-3 flex flex-wrap justify-end gap-2">
                        <template v-if="p.key === 'myflow_site'">
                            <button-base
                                v-if="
                                    p.is_active &&
                                    !detail.is_in_use_for_site &&
                                    detail.site_validated
                                "
                                style-type="secondary"
                                type="button"
                                :disabled="switchingSiteDomain"
                                @click="useForSite"
                            >
                                {{
                                    switchingSiteDomain
                                        ? _mft(
                                              'setting:domains.manage.switching'
                                          )
                                        : _mft(
                                              'setting:domains.manage.useForMyflowSite'
                                          )
                                }}
                            </button-base>
                            <button-base
                                v-else-if="
                                    p.is_active &&
                                    !detail.is_in_use_for_site &&
                                    detail.site_activation_pending
                                "
                                style-type="disabled"
                                type="button"
                                disabled
                            >
                                {{
                                    _mft('setting:domains.manage.waitingForDns')
                                }}
                            </button-base>
                            <button-base
                                v-else-if="
                                    p.is_active && !detail.is_in_use_for_site
                                "
                                style-type="secondary"
                                type="button"
                                :disabled="retryingSiteActivation"
                                @click="retrySiteActivation"
                            >
                                {{
                                    retryingSiteActivation
                                        ? _mft(
                                              'setting:domains.manage.retrying'
                                          )
                                        : _mft(
                                              'setting:domains.manage.retryWebsiteVerification'
                                          )
                                }}
                            </button-base>
                            <button-danger
                                v-if="p.is_active && p.can_remove"
                                type="button"
                                :disabled="removingPresetKey === p.key"
                                @click="removePreset(p)"
                            >
                                {{
                                    removingPresetKey === p.key
                                        ? _mft(
                                              'setting:domains.manage.turningOff'
                                          )
                                        : _mft('setting:domains.manage.turnOff')
                                }}
                            </button-danger>
                            <button-base
                                v-if="!p.is_active"
                                :style-type="
                                    isPresetBlocked(p)
                                        ? 'disabled'
                                        : 'secondary'
                                "
                                type="button"
                                :disabled="isPresetBlocked(p)"
                                @click="openPlan(p)"
                            >
                                {{ _mft('setting:domains.manage.turnOn') }}
                            </button-base>
                        </template>
                        <template v-else-if="p.key === 'myflow_email'">
                            <button-base
                                v-if="
                                    p.is_active &&
                                    !detail.is_in_use_for_email &&
                                    detail.email_validated
                                "
                                style-type="secondary"
                                type="button"
                                :disabled="switchingEmailDomain"
                                @click="useForEmail"
                            >
                                {{
                                    switchingEmailDomain
                                        ? _mft(
                                              'setting:domains.manage.switching'
                                          )
                                        : _mft(
                                              'setting:domains.manage.useAsEmailSender'
                                          )
                                }}
                            </button-base>
                            <button-base
                                v-else-if="
                                    p.is_active &&
                                    !detail.is_in_use_for_email &&
                                    detail.email_activation_pending
                                "
                                style-type="disabled"
                                type="button"
                                disabled
                            >
                                {{
                                    _mft('setting:domains.manage.waitingForDns')
                                }}
                            </button-base>
                            <button-base
                                v-else-if="
                                    p.is_active && !detail.is_in_use_for_email
                                "
                                style-type="secondary"
                                type="button"
                                :disabled="retryingEmailActivation"
                                @click="retryEmailActivation"
                            >
                                {{
                                    retryingEmailActivation
                                        ? _mft(
                                              'setting:domains.manage.retrying'
                                          )
                                        : _mft(
                                              'setting:domains.manage.retryEmailVerification'
                                          )
                                }}
                            </button-base>
                            <button-danger
                                v-if="p.is_active && p.can_remove"
                                type="button"
                                :disabled="removingPresetKey === p.key"
                                @click="removePreset(p)"
                            >
                                {{
                                    removingPresetKey === p.key
                                        ? _mft(
                                              'setting:domains.manage.turningOff'
                                          )
                                        : _mft('setting:domains.manage.turnOff')
                                }}
                            </button-danger>
                            <button-base
                                v-if="!p.is_active"
                                :style-type="
                                    isPresetBlocked(p)
                                        ? 'disabled'
                                        : 'secondary'
                                "
                                type="button"
                                :disabled="isPresetBlocked(p)"
                                @click="openPlan(p)"
                            >
                                {{ _mft('setting:domains.manage.turnOn') }}
                            </button-base>
                        </template>
                        <template v-else-if="p.key === 'redirect'">
                            <button-base
                                :style-type="
                                    isPresetBlocked(p) && !p.is_active
                                        ? 'disabled'
                                        : 'secondary'
                                "
                                type="button"
                                :disabled="isPresetBlocked(p) && !p.is_active"
                                @click="openRedirectForm(p)"
                            >
                                {{
                                    p.is_active
                                        ? _mft(
                                              'setting:domains.manage.editRedirect'
                                          )
                                        : _mft(
                                              'setting:domains.manage.setUpRedirect'
                                          )
                                }}
                            </button-base>
                            <button-danger
                                v-if="p.is_active && p.can_remove"
                                type="button"
                                :disabled="removingPresetKey === p.key"
                                @click="removePreset(p)"
                            >
                                {{
                                    removingPresetKey === p.key
                                        ? _mft(
                                              'setting:domains.manage.turningOff'
                                          )
                                        : _mft('setting:domains.manage.turnOff')
                                }}
                            </button-danger>
                        </template>
                        <button-base
                            v-else
                            :style-type="
                                isPresetBlocked(p) || p.is_active
                                    ? 'disabled'
                                    : 'secondary'
                            "
                            type="button"
                            :disabled="isPresetBlocked(p) || p.is_active"
                            @click="openPlan(p)"
                        >
                            {{
                                p.is_active
                                    ? _mft('setting:domains.manage.configured')
                                    : _mft('setting:domains.manage.turnOn')
                            }}
                        </button-base>
                    </div>
                </card-basic>
            </div>
        </div>

        <card-sub v-for="p in activePresetsWithSteps" :key="`steps-${p.key}`">
            <h3 class="text-sm font-semibold text-blue-900">
                {{
                    _mft('setting:domains.manage.finishSettingUp', {
                        label: presetLabel(p),
                    })
                }}
            </h3>
            <p class="mt-1 text-xs text-blue-800">
                {{ _mft('setting:domains.manage.externalStepsDescription') }}
            </p>
            <ol class="mt-3 space-y-3 text-sm text-blue-900">
                <li
                    v-for="(s, i) in p.next_steps"
                    :key="i"
                    class="rounded-md border border-blue-200 bg-white p-3"
                >
                    <div class="font-medium">
                        {{ presetNextStepText(p, s, i, 'title') }}
                    </div>
                    <div class="mt-1 text-xs text-gray-700">
                        {{ presetNextStepText(p, s, i, 'description') }}
                    </div>
                    <a
                        v-if="s.url"
                        :href="s.url"
                        target="_blank"
                        rel="noopener"
                        class="mt-2 inline-block rounded-md bg-brandPrimary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brandPrimary-700"
                    >
                        {{ presetNextStepText(p, s, i, 'cta') }}
                    </a>
                </li>
            </ol>
        </card-sub>

        <card-basic>
            <button
                type="button"
                class="flex w-full items-center justify-between"
                @click="showAdvanced = !showAdvanced"
            >
                <span class="font-medium">{{
                    _mft('setting:domains.manage.advancedDnsEditor')
                }}</span>
                <i
                    :class="[
                        'fa-regular',
                        showAdvanced ? 'fa-chevron-up' : 'fa-chevron-down',
                    ]"
                ></i>
            </button>
            <div v-if="showAdvanced" class="border-t py-4 space-y-3 mt-4">
                <div class="flex items-center justify-between">
                    <p class="text-xs text-gray-500">
                        {{
                            _mft('setting:domains.manage.presetRecordsReadonly')
                        }}
                    </p>
                    <button
                        type="button"
                        class="rounded-md border px-3 py-1 text-xs font-medium hover:bg-gray-50"
                        @click="startEditRecord(null)"
                    >
                        + {{ _mft('setting:domains.manage.addRecord') }}
                    </button>
                </div>

                <div v-if="loadingRecords" class="text-sm text-gray-500">
                    {{ _mft('shared:loading') }}
                </div>
                <table v-else class="w-full text-left text-sm">
                    <thead class="border-b text-xs text-gray-500">
                        <tr>
                            <th class="py-2 pr-3">
                                {{ _mft('setting:domains.manage.record.type') }}
                            </th>
                            <th class="py-2 pr-3">
                                {{ _mft('setting:domains.manage.record.host') }}
                            </th>
                            <th class="py-2 pr-3">
                                {{
                                    _mft('setting:domains.manage.record.value')
                                }}
                            </th>
                            <th class="py-2 pr-3">TTL</th>
                            <th class="py-2 pr-3">
                                {{
                                    _mft(
                                        'setting:domains.manage.record.priorityShort'
                                    )
                                }}
                            </th>
                            <th class="py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="r in records"
                            :key="r.id"
                            class="border-b last:border-0"
                        >
                            <td class="py-2 pr-3 font-mono text-xs">
                                {{ r.type }}
                            </td>
                            <td class="py-2 pr-3 font-mono text-xs">
                                {{ r.host || '@' }}
                            </td>
                            <td
                                class="w-full max-w-0 py-2 pr-3 font-mono text-xs"
                            >
                                <div class="truncate" :title="r.answer">
                                    {{ r.answer }}
                                </div>
                            </td>
                            <td class="py-2 pr-3 text-xs">{{ r.ttl }}</td>
                            <td class="py-2 pr-3 text-xs">
                                {{ r.priority ?? '' }}
                            </td>
                            <td class="whitespace-nowrap py-2 text-right">
                                <button
                                    type="button"
                                    class="text-xs text-gray-500 mf-link mr-3"
                                    @click="startEditRecord(r)"
                                >
                                    {{ _mft('shared:action.edit') }}
                                </button>
                                <button
                                    type="button"
                                    class="text-xs text-red-600 mf-link"
                                    @click="deleteRecord(r)"
                                >
                                    {{ _mft('shared:action.delete') }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </card-basic>

        <!-- Redirect editor modal -->
        <div
            v-if="redirectForm"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
            <form class="w-full max-w-lg" @submit.prevent="submitRedirectForm">
                <card-basic>
                    <h3 class="text-lg font-semibold">
                        {{
                            redirectForm.preset.is_active
                                ? _mft('setting:domains.manage.editRedirect')
                                : _mft('setting:domains.manage.setUpRedirect')
                        }}
                    </h3>
                    <p class="mt-1 text-sm text-gray-600">
                        {{
                            _mft('setting:domains.manage.redirectDescription', {
                                domain,
                            })
                        }}
                    </p>
                    <div class="mt-4">
                        <input-text
                            :label="
                                _mft('setting:domains.manage.destinationUrl')
                            "
                            v-model:content="redirectForm.targetUrl"
                            placeholder="https://example.com"
                            required
                        />
                    </div>
                    <div
                        v-if="redirectFormError"
                        class="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                    >
                        {{ redirectFormError }}
                    </div>
                    <div class="mt-5 flex justify-end gap-2">
                        <button-secondary
                            type="button"
                            @click="redirectForm = null"
                            >{{
                                _mft('shared:action.cancel')
                            }}</button-secondary
                        >
                        <button-success type="submit">{{
                            _mft('setting:domains.manage.reviewChanges')
                        }}</button-success>
                    </div>
                </card-basic>
            </form>
        </div>

        <!-- Preset confirm modal -->
        <div
            v-if="planning"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
            <div class="w-full max-w-lg">
                <card-basic>
                    <h3 class="text-lg font-semibold">
                        {{ presetLabel(planning.preset) }}
                    </h3>
                    <p class="mt-1 text-sm text-gray-600">
                        {{
                            _mft(
                                'setting:domains.manage.reviewPresetChangesDescription'
                            )
                        }}
                    </p>
                    <ul class="mt-3 space-y-2 text-xs">
                        <li v-for="(c, i) in planning.changes" :key="i">
                            <card-sub style-type="transparent">
                                <div class="flex items-center gap-2">
                                    <span
                                        class="inline-flex rounded px-2 py-0.5 text-[10px] font-medium uppercase"
                                        :class="{
                                            'bg-green-100 text-green-800':
                                                c.op === 'create',
                                            'bg-red-100 text-red-800':
                                                c.op === 'delete',
                                            'bg-blue-100 text-blue-800':
                                                c.op === 'update',
                                            'bg-gray-100 text-gray-600':
                                                c.op === 'noop',
                                        }"
                                        >{{
                                            _mft(
                                                `setting:domains.manage.changeOperation.${c.op}`
                                            )
                                        }}</span
                                    >
                                    <code class="truncate select-all"
                                        >{{ c.type }} {{ c.host }} →
                                        {{ c.answer }}</code
                                    >
                                </div>
                            </card-sub>
                        </li>
                        <li
                            v-if="planning.changes.length === 0"
                            class="text-gray-500"
                        >
                            {{ _mft('setting:domains.manage.nothingToChange') }}
                        </li>
                    </ul>

                    <card-sub
                        v-if="
                            planning.preset.next_steps &&
                            planning.preset.next_steps.length
                        "
                        class="mt-4"
                        style-type="info"
                    >
                        <h4 class="text-xs font-semibold text-blue-900">
                            {{ _mft('setting:domains.manage.afterTurningOn') }}
                        </h4>
                        <ol class="mt-2 space-y-2 text-xs text-blue-900">
                            <li
                                v-for="(s, i) in planning.preset.next_steps"
                                :key="i"
                            >
                                <div class="font-medium">
                                    {{
                                        presetNextStepText(
                                            planning.preset,
                                            s,
                                            i,
                                            'title'
                                        )
                                    }}
                                </div>
                                <div class="text-blue-800">
                                    {{
                                        presetNextStepText(
                                            planning.preset,
                                            s,
                                            i,
                                            'description'
                                        )
                                    }}
                                </div>
                                <a
                                    v-if="s.url"
                                    :href="s.url"
                                    target="_blank"
                                    rel="noopener"
                                    class="mf-link text-xs"
                                >
                                    {{
                                        presetNextStepText(
                                            planning.preset,
                                            s,
                                            i,
                                            'cta'
                                        )
                                    }}
                                    →
                                </a>
                            </li>
                        </ol>
                    </card-sub>
                    <div class="mt-5 flex justify-end gap-2">
                        <button-secondary @click="planning = null">{{
                            _mft('shared:action.cancel')
                        }}</button-secondary>
                        <button-success
                            type="button"
                            :disabled="
                                applying || planning.changes.length === 0
                            "
                            @click="applyPlan"
                        >
                            {{
                                applying
                                    ? _mft('setting:domains.manage.applying')
                                    : _mft(
                                          'setting:domains.manage.applyChanges'
                                      )
                            }}
                        </button-success>
                    </div>
                </card-basic>
            </div>
        </div>

        <!-- Record editor modal -->
        <div
            v-if="editing"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
            <form class="w-full max-w-md" @submit.prevent="saveRecord">
                <card-basic>
                    <h3 class="text-lg font-semibold mb-3">
                        {{
                            editing.mode === 'new'
                                ? _mft('setting:domains.manage.addDnsRecord')
                                : _mft('setting:domains.manage.editDnsRecord')
                        }}
                    </h3>

                    <input-select
                        :label="_mft('setting:domains.manage.record.type')"
                        v-model:content="editing.record.type"
                        :options="recordTypeOptions"
                    />
                    <input-text
                        :label="_mft('setting:domains.manage.hostRootHint')"
                        v-model:content="editing.record.host"
                        required
                    />
                    <input-text
                        :label="_mft('setting:domains.manage.record.value')"
                        v-model:content="editing.record.answer"
                        required
                    />
                    <div class="grid grid-cols-2 gap-3">
                        <input-text
                            :label="_mft('setting:domains.manage.ttlSeconds')"
                            v-model:content="editing.record.ttl"
                            inputtype="number"
                            required
                        />
                        <input-text
                            v-if="['MX', 'SRV'].includes(editing.record.type)"
                            :label="
                                _mft('setting:domains.manage.record.priority')
                            "
                            v-model:content="editing.record.priority"
                            inputtype="number"
                        />
                    </div>

                    <div class="flex justify-end gap-2 pt-2">
                        <button-secondary
                            type="button"
                            @click="editing = null"
                            >{{
                                _mft('shared:action.cancel')
                            }}</button-secondary
                        >
                        <button-success type="submit">{{
                            _mft('shared:action.save')
                        }}</button-success>
                    </div>
                </card-basic>
            </form>
        </div>
    </div>
</template>
